import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, AreaPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsLegend } from "@mui/x-charts/ChartsLegend";
import { ChartsSurface } from "@mui/x-charts/ChartsSurface";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { useCfmContext } from "../../../Context/CfmContext";
import { getAgeFromDOB } from "../../../Context/Utils/GetAgeFromDOB";
import { hexToRgba } from "./GetGradientColor";
import "../../../../../../css/CashflowModeller2/Charts/Graphs.css";
import {useRef} from "react";
import { ChartDataProviderPro } from "@mui/x-charts-pro/ChartDataProviderPro";
import CustomChartWrapper from "../Utils/CustomWrapper";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import useGraphHeight from "../Utils/useGraphHeight";



/**
 * Renders an investment graph for a selected person, displaying fund values, contributions, and withdrawals over time.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.selectedPerson - The name (or prefix) of the person whose investments are to be displayed.
 * @param {boolean} [props.animated=false] - Whether to animate the chart transitions.
 * @param {boolean} [props.isPrint=false] - Whether the chart is being rendered for print (affects sizing).
 * @param {string} [props.idOverride] - Optional override for the chart container's DOM id.
 * @returns {JSX.Element} The rendered investment graph component.
 *
 * @example
 * <InvestmentGraph selectedPerson="John" animated={true} isPrint={false} />
 */
const InvestmentGraph = ({
  selectedPerson,
  animated = false,
  isPrint = false,
  idOverride,
  apiRef = null,
}) => {
  const { state, dispatch } = useCfmContext();
  const person = state.details?.find((d) => d.name?.startsWith(selectedPerson));
  const currentAge = getAgeFromDOB(person?.dob) ?? 18;
  const chartOnlyRef = useRef();

  // console.log("InvestmentGraph rendered for:", state);

  const hasInvestmensts = (state.investments || []).filter(
    (entry) => person.name === entry.name
  );

  const userInvestments =
    hasInvestmensts.length > 0
      ? (state.investmentResults || []).filter(
          (entry) => person.name === entry.name
        )
      : [];

  const allAgesSet = new Set();
  userInvestments.forEach((inv) => {
    inv.investmentFunds?.forEach((row) => {
      if (row.age >= currentAge) allAgesSet.add(row.age);
    });
  });
  const allAges = Array.from(allAgesSet).sort((a, b) => a - b);

  const colors = [
    {
      line: "#355c8d",
      bar: "#22c55e",
      adhoc: "#9333ea",
      contribution: "#facc15",
    },
    {
      line: "#A1887F",
      bar: "#e5b368",
      adhoc: "#ff8a80",
      contribution: "#90caf9",
    },
    {
      line: "#ff8a65",
      bar: "#fdd835",
      adhoc: "#ce93d8",
      contribution: "#ffee58",
    },
    {
      line: "#ba68c8",
      bar: "#9575cd",
      adhoc: "#e1bee7",
      contribution: "#fff176",
    },
  ];

  const series = userInvestments.flatMap((inv, index) => {
    const label = `${inv.type}` || `Investment ${index + 1}`;
    const fundMap = new Map();

    inv.investmentFunds?.forEach((row) => {
      if (row.age >= currentAge) {
        fundMap.set(row.age, {
          fundValue: Math.max(
            0,
            parseFloat(
              String(row.fundValue || "0").replace(/[^0-9.-]+/g, "")
            ) || 0
          ),

          contribution: parseFloat(
            (row.contribution || "0").replace(/[^0-9.-]+/g, "")
          ),

          withdrawals: parseFloat(
            (row.regularWithdrawals || "0").replace(/[^0-9.-]+/g, "")
          ),
          adhocWithdrawals: parseFloat(
            (row.adhocWithdrawals || "0").replace(/[^0-9.-]+/g, "")
          ),
          adhocContribution: parseFloat(
            (row.adhocInvestment || "0").replace(/[^0-9.-]+/g, "")
          ),
        });
      }
    });

    return [
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.withdrawals || 0),
        label: `${label} - Withdrawals`,
        color: colors[index % colors.length].bar,
        labelMarkType: "circle",
        // yAxisId: "rightAxis",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocWithdrawals || 0),
        label: `${label} - Adhoc Withdrawals`,
        color: colors[index % colors.length].adhoc,
        labelMarkType: "circle",
        // yAxisId: "rightAxis",
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.adhocContribution || 0),
        label: `${label} - Adhoc Contributions`,
        color: colors[index % colors.length].contribution,
        labelMarkType: "circle",
      },
      {
        type: "line",
        area: true,
        data: allAges.map((age) => fundMap.get(age)?.fundValue || 0),
        label: `${label} - Fund Value`,
        color: colors[index % colors.length].line,
      },
      {
        type: "bar",
        stack: `${label}-stack`,
        data: allAges.map((age) => fundMap.get(age)?.contribution || 0),
        label: `${label} - Contributions`,
        color: colors[index % colors.length].contribution,
        labelMarkType: "circle",
      },
    ];
  });

  const placeholderSeries = [
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Withdrawals",
      color: colors[0].bar,
    },
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Adhoc Withdrawals",
      color: colors[0].adhoc,
    },
    {
      type: "bar",
      stack: "investment",
      data: [0],
      label: "Adhoc Contributions",
      color: colors[0].contribution,
    },
    {
      type: "line",
      data: [0],
      label: "Fund Value",
      color: colors[0].line,
    },
  ];

  const chartSeries = series.length > 0 ? series : placeholderSeries;
  const chartAges = allAges.length > 0 ? allAges : [];

  const visibleSeries = chartSeries.filter((s) =>
    s.data.some((v) => Number(v ?? 0) !== 0)
  );

  const graphHeight = useGraphHeight(isPrint);

  return (
    <div
      ref={chartOnlyRef}
      // className="cfm-graph-wrapper"
      style={{
        width: isPrint ? 800 : "100%",
        height: isPrint ? 500 : "100%",
        backgroundColor: "transparent",
      }}
      id={idOverride || "inv-graph"}
    >
      {/* <Button
        onClick={() => apiRef.current.exportAsImage()}
        variant="contained"
        sx={{ mb: 1 }}
      /> */}

      {/* {allAges.length > 0 ? ( */}
      <ChartDataProviderPro
        key={selectedPerson}
        series={visibleSeries}
        apiRef={apiRef}
        height={graphHeight}
        xAxis={[
          {
            id: "age-axis",
            data: chartAges,
            scaleType: "band",
            label: "Age",
          },
        ]}
        yAxis={[
          {
            id: "value-axis",
            label: "Fund Value",
            valueFormatter: (val) => {
              if (val >= 1_000_000) return `£${(val / 1_000_000).toFixed(1)}m`;
              if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
              return `£${val}`;
            },
            width: 70,
          },
          // {
          //   id: "rightAxis",
          //   label: "Withdrawals",
          //   position: "right",
          //   valueFormatter: (val) => {
          //     if (val >= 1_000_000)
          //       return `£${(val / 1_000_000).toFixed(1)}m`;
          //     if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
          //     return `£${val}`;
          //   },
          //   width: 70,
          // },
          // {
          //   id: "rightAdhocAxis",
          //   label: "Adhoc Withdrawals",
          //   position: "right",
          //   valueFormatter: (val) => {
          //     if (val >= 1_000_000)
          //       return `£${(val / 1_000_000).toFixed(1)}m`;
          //     if (val >= 1_000) return `£${(val / 1_000).toFixed(0)}k`;
          //     return `£${val}`;
          //   },
          //   width: 70,
          // }
        ]}
      >
        <CustomChartWrapper isApiPrint={apiRef ? true : false}>
          <ChartsLegend
            direction="horizontal"
            position="top"
            slotProps={{
              legend: {
                sx: {
                  fontSize: 12,
                  color: "#081e56",
                  fontFamily: "'Open Sans'",
                  fontWeight: 500,
                },
              },
            }}
          />
          <ChartsSurface>
            <defs>
              {chartSeries
                .filter((s) => s.type === "line")
                .map((s, idx) => (
                  <linearGradient
                    key={`grad-${idx}`}
                    id={`grad-${idx}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={hexToRgba(s.color, 0.2)} />
                    <stop offset="100%" stopColor={hexToRgba(s.color, 0)} />
                  </linearGradient>
                ))}
            </defs>

            <BarPlot
              // animated={animated.toString()}
              // animated="true"
              springconfig={{ tension: 80, friction: 80 }}
              style={{
                opacity: 0,
                animation: "fadeIn  forwards ease-in-out",
              }}
            />

            {chartSeries
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <AreaPlot
                  key={`area-${idx}`}
                  seriesid={s.label}
                  // animated={animated.toString()}
                  // animated="true"
                  springconfig={{ tension: 80, friction: 80 }}
                  style={{
                    opacity: 0,
                    animation: "fadeIn  forwards ease-in-out",
                  }}
                  slots={{
                    area: ({ d, style }) => (
                      <path
                        d={d}
                        style={style}
                        fill={`url(#grad-${idx})`}
                        stroke="none"
                      />
                    ),
                  }}
                />
              ))}

            {chartSeries
              .filter((s) => s.type === "line")
              .map((s, idx) => (
                <LinePlot
                  key={`line-${idx}`}
                  seriesid={s.label}
                  // animated={animated.toString()}
                  slotProps={{
                    line: {
                      style: {
                        strokeWidth: 4,
                      },
                    },
                  }}
                  springconfig={{ tension: 80, friction: 80 }}
                  style={{
                    opacity: 0,
                    animation: "fadeIn  forwards ease-in-out",
                  }}
                />
              ))}

            <ChartsXAxis
              axisId="age-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />

            <ChartsYAxis
              axisId="value-axis"
              sx={{
                [`& .${axisClasses.tickLabel}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  fill: "#081e56",
                },
                [`& .${axisClasses.label}`]: {
                  fontFamily: "'Open Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 800,
                  fill: "#081e56",
                },
              }}
            />
            {/* <ChartsYAxis axisId="rightAxis" /> */}
            {/* <ChartsYAxis axisId="rightAdhocAxis" /> */}
            <ChartsTooltip />
          </ChartsSurface>
        </CustomChartWrapper>
      </ChartDataProviderPro>
      {/* ) : (
        <div />
      )} */}
    </div>
  );
};

export default InvestmentGraph;
